import { skipToken, useQuery } from "@tanstack/react-query";
import type { VenueGroupConfig } from "../types/VenueGroup";
import type { VenueUserConfig } from "../types/VenueUser";
import {
  organisationDataQueryKey,
  selectedOrganisationQueryKey
} from "../config/queryConfig";
import {
  OrganisationData,
  selectOrganisationData
} from "../utils/organisationDataCache";
import { useAppSelector } from "../store/hooks";
import { selectCollinsUser } from "../store/slices/sessionSlice";

type OrganisationDataResult = {
  data: OrganisationData | undefined;
  dataUpdatedAt: number;
};

const useOrganisationDataQueries = () => {
  const user = useAppSelector(selectCollinsUser);
  const organisationQuery = useQuery({
    queryKey: organisationDataQueryKey(user),
    queryFn: skipToken,
    select: selectOrganisationData
  });
  const selectionQuery = useQuery({
    queryKey: selectedOrganisationQueryKey(user),
    queryFn: skipToken,
    select: selectOrganisationData,
    staleTime: Infinity,
    gcTime: Infinity
  });

  return { organisationQuery, selectionQuery };
};

const pickNewestNonNull = <Value>(
  organisationQuery: OrganisationDataResult,
  selectionQuery: OrganisationDataResult,
  selectValue: (data: OrganisationData) => Value | null
): Value | null => {
  const organisationValue = organisationQuery.data
    ? selectValue(organisationQuery.data)
    : null;
  const selectionValue = selectionQuery.data
    ? selectValue(selectionQuery.data)
    : null;

  if (organisationValue === null) return selectionValue;
  if (selectionValue === null) return organisationValue;

  return selectionQuery.dataUpdatedAt > organisationQuery.dataUpdatedAt
    ? selectionValue
    : organisationValue;
};

/** Reads the venue-group configuration for the current organisation. */
export const useVenueGroupConfig = (): VenueGroupConfig | null => {
  const { organisationQuery, selectionQuery } = useOrganisationDataQueries();

  return pickNewestNonNull(
    organisationQuery,
    selectionQuery,
    (data) => data.venueGroupConfig
  );
};

/** Reads the venue-user configuration for the current organisation. */
export const useVenueUserConfig = (): VenueUserConfig | null => {
  const { organisationQuery, selectionQuery } = useOrganisationDataQueries();

  return pickNewestNonNull(
    organisationQuery,
    selectionQuery,
    (data) => data.venueUserConfig
  );
};
